import { createClient } from '@/prismicio';
import { asText, Content, isFilled } from '@prismicio/client';

import { retrieveProductPrice } from '@preventx/prismic-services';

// Types
import { ProductVariantOption } from '@/types/products/productVariants';
import { ProductConfiguration } from '@/types/products/productConfiguration';

// Services
import { colour } from '@/services/utils/colour';
import { retrieveIcon } from '@/services/utils/icons';

export const retrieveProductVariants = async (
  variants: Content.ProductsHeroHeadlineSliceWithProductVariantsPrimaryProductVariantsItem[],
): Promise<ProductVariantOption[]> => {
  const client = await createClient();

  const productUIDs = variants
    .map(({ product }) => {
      if (!isFilled.contentRelationship(product)) {
        return null;
      }

      return product.uid;
    })
    .filter((product) => product !== null && product !== undefined);

  const products = await client
    .getAllByUIDs('product', productUIDs)
    .catch(() => []);

  const mappedProducts = await Promise.all(
    products.map(async (product, index) => {
      const { uid: productKey, id } = product;

      let unitPrice: string | number = '';

      try {
        const priceDetails = await retrieveProductPrice({
          currency: 'GBP',
          productKey: productKey,
        });

        if (!priceDetails) {
          return null;
        }

        unitPrice = priceDetails;
      } catch {
        return null;
      }

      const productVariant = variants.find(({ product }) => {
        if (!isFilled.contentRelationship(product)) {
          return null;
        }

        return product.id === id;
      });

      if (!productVariant) {
        return null;
      }

      const { product_variant_name, application } = product.data;

      if (application === 'Default') {
        return null;
      }

      return {
        checked: false,
        price: unitPrice,
        productKey: productKey,
        application: application,
        id: `${product.id}-${index + 1}`,
        label: asText(product_variant_name),
      } satisfies ProductVariantOption;
    }),
  );

  return mappedProducts.filter((product) => product !== null);
};

export const retrieveProductsConfiguration = async (
  products: Content.ProductsSliceDefaultPrimaryProductsItem[],
): Promise<(ProductConfiguration | null)[]> => {
  const client = await createClient();

  const response = await Promise.all(
    products.map(async (item): Promise<ProductConfiguration | null> => {
      if (!isFilled.contentRelationship(item.product)) {
        return null;
      }

      const productKey = item.product.uid;

      if (!productKey) {
        return null;
      }

      const response = await client
        .getByUID('product', productKey)
        .catch(() => null);

      if (!response || !response.data) {
        return null;
      }

      const { colour_scheme } = response.data;

      let unitPrice: string | number = '';

      try {
        const priceDetails = await retrieveProductPrice({
          currency: 'GBP',
          productKey: productKey,
        });

        if (!priceDetails) {
          return null;
        }

        unitPrice = priceDetails;
      } catch {
        return null;
      }

      return {
        link: item.link,
        price: unitPrice,
        data: response.data,
        id: item.product.id,
        colourScheme: colour(colour_scheme),
        imageMeta: {
          label: item.image_meta_label,
          icon:
            item.image_meta_icon && item.image_meta_icon !== 'None'
              ? retrieveIcon(item.image_meta_icon)
              : undefined,
        },
      };
    }),
  );

  return response;
};
